import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  ShoppingCart,
  AttachMoney,
  Inventory,
  Star,
  LocalShipping,
  PendingActions,
  CheckCircle,
  TrendingUp,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { vendorAPI } from 'services/api';

const StatCard = ({ title, value, icon, color, subtitle }) => (
  <Card
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      border: `1px solid ${color}30`,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}20`, color, width: 48, height: 48 }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const statusColors = {
  pending: '#FF9800',
  escrow_reserved: '#2196F3',
  invoice_verified: '#9C27B0',
  admin_approved: '#00BCD4',
  confirmed: '#4CAF50',
  processing: '#FF5722',
  shipped: '#3F51B5',
  delivered: '#4CAF50',
  cancelled: '#F44336',
  rejected: '#F44336',
};

const VendorDashboard = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vendorAPI.getDashboard();
      setDashboard(response.data);
    } catch (err) {
      console.error('Failed to load vendor dashboard:', err);
      setError('Failed to load dashboard data. Please try again.');
      // Set fallback data
      setDashboard({
        vendor_id: null,
        business_name: user?.name || 'Vendor',
        total_orders: 0,
        pending_orders: 0,
        processing_orders: 0,
        completed_orders: 0,
        total_revenue: 0,
        pending_payments: 0,
        total_products: 0,
        average_rating: 0,
        recent_orders: [],
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  const data = dashboard || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          🏪 Vendor Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {data.business_name || user?.name}! Here's your business overview.
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={data.total_orders}
            icon={<ShoppingCart />}
            color="#2196F3"
            subtitle={`${data.pending_orders} pending`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={`₹${(data.total_revenue || 0).toLocaleString('en-IN')}`}
            icon={<AttachMoney />}
            color="#4CAF50"
            subtitle="From delivered orders"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Products"
            value={data.total_products}
            icon={<Inventory />}
            color="#FF9800"
            subtitle="Listed in marketplace"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rating"
            value={data.average_rating ? data.average_rating.toFixed(1) : 'N/A'}
            icon={<Star />}
            color="#9C27B0"
            subtitle="Customer rating"
          />
        </Grid>
      </Grid>

      {/* Order Status Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PendingActions sx={{ color: '#FF9800', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Pending Orders
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" color="#FF9800">
                {data.pending_orders}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Awaiting your action
              </Typography>
              <LinearProgress
                variant="determinate"
                value={data.total_orders ? (data.pending_orders / data.total_orders) * 100 : 0}
                sx={{ mt: 2, height: 8, borderRadius: 4, bgcolor: '#FFF3E0', '& .MuiLinearProgress-bar': { bgcolor: '#FF9800' } }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalShipping sx={{ color: '#2196F3', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  In Progress
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" color="#2196F3">
                {data.processing_orders}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Being processed or shipped
              </Typography>
              <LinearProgress
                variant="determinate"
                value={data.total_orders ? (data.processing_orders / data.total_orders) * 100 : 0}
                sx={{ mt: 2, height: 8, borderRadius: 4, bgcolor: '#E3F2FD', '& .MuiLinearProgress-bar': { bgcolor: '#2196F3' } }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: '#4CAF50', mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Completed
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="bold" color="#4CAF50">
                {data.completed_orders}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Successfully delivered
              </Typography>
              <LinearProgress
                variant="determinate"
                value={data.total_orders ? (data.completed_orders / data.total_orders) * 100 : 0}
                sx={{ mt: 2, height: 8, borderRadius: 4, bgcolor: '#E8F5E9', '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50' } }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Payments Alert */}
      {(data.pending_payments || 0) > 0 && (
        <Alert
          severity="info"
          icon={<TrendingUp />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          You have <strong>₹{(data.pending_payments).toLocaleString('en-IN')}</strong> in pending payments.
        </Alert>
      )}

      {/* Recent Orders Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Recent Orders
          </Typography>
          {data.recent_orders && data.recent_orders.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell><strong>Order #</strong></TableCell>
                    <TableCell><strong>Buyer</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recent_orders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.buyer_name}</TableCell>
                      <TableCell>
                        {order.order_date
                          ? new Date(order.order_date).toLocaleDateString('en-IN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        ₹{(order.total_amount || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status?.replace(/_/g, ' ').toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: `${statusColors[order.status] || '#757575'}20`,
                            color: statusColors[order.status] || '#757575',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ShoppingCart sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
              <Typography color="text.secondary">
                No orders yet. Your orders will appear here.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VendorDashboard;
